/** B2B sharing overview: incoming/outgoing counts, latest share, shortcuts. */
import React from 'react';
import { TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import type { SharedByMeDocument } from '@/types';
import { webStyles } from '../libraryStyles';

export interface LibraryShareCenterProps {
  isCompact: boolean;
  isMentorOrAdmin: boolean;
  canShare: boolean;
  sharedWithMeCount: number;
  sharedByMeCount: number;
  latestSharedOut: SharedByMeDocument | undefined;
  onOpenInbox: () => void;
  onManageLatest: () => void;
}

export function LibraryShareCenter({
  isCompact,
  isMentorOrAdmin,
  canShare,
  sharedWithMeCount,
  sharedByMeCount,
  latestSharedOut,
  onOpenInbox,
  onManageLatest,
}: LibraryShareCenterProps) {
  const { t } = useTranslation();
  const recipientLabel = (item: SharedByMeDocument) => {
    if (item.recipient.type === 'all_students') return t('share.allStudents');
    if (item.recipient.type === 'all_members') return t('share.allMembers');
    return item.recipient.full_name || t('library.unknown');
  };
  return (
    <View
      style={[webStyles.shareCenter, isCompact && webStyles.shareCenterCompact]}
      nativeID="library_share_center"
      testID="library_share_center"
    >
      <View style={webStyles.shareCenterIntro}>
        <View style={webStyles.shareCenterIcon}>
          <MaterialIcons name="folder-shared" size={30} color={reviewDesk.accent} />
        </View>
        <View style={webStyles.shareCenterCopy}>
          <Text style={webStyles.shareCenterTitle}>
            {isMentorOrAdmin
              ? t('library.sharingCenterMentorTitle')
              : t('library.sharingCenterStudentTitle')}
          </Text>
          <Text style={webStyles.shareCenterText}>
            {isMentorOrAdmin
              ? t('library.sharingCenterMentorCopy')
              : t('library.sharingCenterStudentCopy')}
          </Text>
          {latestSharedOut && (
            <Text style={webStyles.shareCenterMeta} numberOfLines={1}>
              {t('library.lastSharedTo')}: {latestSharedOut.original_filename} · {recipientLabel(latestSharedOut)}
            </Text>
          )}
        </View>
      </View>
      <View style={webStyles.shareMetricRow}>
        <View style={webStyles.shareMetric}>
          <Text style={webStyles.shareMetricValue}>{sharedWithMeCount}</Text>
          <Text style={webStyles.shareMetricLabel}>{t('library.sharedWithMeShort')}</Text>
        </View>
        <View style={webStyles.shareMetric}>
          <Text style={webStyles.shareMetricValue}>{sharedByMeCount}</Text>
          <Text style={webStyles.shareMetricLabel}>{t('library.sharedByMeShort')}</Text>
        </View>
      </View>
      <View style={webStyles.shareCenterActions}>
        <TouchableOpacity
          style={webStyles.shareCenterPrimary}
          onPress={onOpenInbox}
          accessibilityRole="button"
        >
          <Text className="text-white" style={webStyles.shareCenterPrimaryText}>
            {t('library.openSharedInbox')}
          </Text>
          <MaterialIcons name="chevron-right" size={18} color="#FFFFFF" />
        </TouchableOpacity>
        {latestSharedOut ? (
          <TouchableOpacity
            style={webStyles.shareCenterSecondary}
            onPress={onManageLatest}
            accessibilityRole="button"
          >
            <Text style={webStyles.shareCenterSecondaryText}>
              {t('library.manageLatestShare')}
            </Text>
            <MaterialIcons name="manage-accounts" size={18} color={reviewDesk.accent} />
          </TouchableOpacity>
        ) : (
          <Text style={webStyles.shareCenterEmpty}>
            {canShare
              ? t('library.sharedOutEmpty')
              : t('library.sharedOutEmptyReadonly')}
          </Text>
        )}
      </View>
    </View>
  );
}
